import { useEffect, useRef, useCallback } from 'react';
import { auth as firebaseAuth } from '../firebase';
import { getPendingChanges, removePendingChange } from '../utils/helpers';
import { postJson, putJson, del as apiDelete } from '../utils/apiClient';

/**
 * Force a token refresh from Firebase Auth before replaying queued changes,
 * so the apiClient interceptor picks up a fresh token. Returns true if we have
 * a token to send (either freshly refreshed, or one already in localStorage).
 */
async function ensureFreshToken() {
  try {
    const fbUser = firebaseAuth.currentUser;
    if (fbUser) {
      const fresh = await fbUser.getIdToken(true);
      localStorage.setItem('kanto-token', fresh);
      return true;
    }
  } catch (e) {
    console.log('[OfflineQueue] Firebase token refresh failed, using stored token');
  }
  return !!localStorage.getItem('kanto-token');
}

/**
 * Hook that processes queued offline changes when connectivity returns.
 * Replays each change via API in creation order (sequential).
 * Drops changes after 3 failed retries.
 */
export default function useOfflineQueue({ isOnline, cache, setCache, showNotification }) {
  const isProcessingRef = useRef(false);
  const prevOnlineRef = useRef(isOnline);

  console.log('[OfflineQueue] Hook render — isOnline:', isOnline, 'pendingChanges:', Object.keys(cache.pendingChanges || {}).length);

  // Keep a live ref to cache so processQueue always reads the latest
  const cacheRef = useRef(cache);
  useEffect(() => { cacheRef.current = cache; }, [cache]);

  const processQueue = useCallback(async () => {
    if (isProcessingRef.current) {
      console.log('[OfflineQueue] Already processing, skipping');
      return;
    }

    const pending = getPendingChanges(cacheRef.current);
    if (pending.length === 0) return;

    // Refresh token before replaying so apiClient interceptor sends fresh creds
    const hasToken = await ensureFreshToken();
    if (!hasToken) {
      console.log('[OfflineQueue] No auth token available, will retry');
      return;
    }

    console.log(`[OfflineQueue] Processing ${pending.length} queued changes`);
    isProcessingRef.current = true;
    let synced = 0;
    let failed = 0;

    for (const change of pending) {
      try {
        switch (change.type) {
          case 'createItem': {
            console.log(`[OfflineQueue] Syncing createItem: "${change.payload.content}" to list ${change.listId}`);
            await postJson(`/lists/${change.listId}/items`, { content: change.payload.content });
            break;
          }
          case 'updateItem': {
            console.log(`[OfflineQueue] Syncing updateItem: ${change.payload.itemId}`);
            await putJson(`/lists/${change.listId}/items/${change.payload.itemId}`, change.payload.updates);
            break;
          }
          case 'deleteItem': {
            console.log(`[OfflineQueue] Syncing deleteItem: ${change.payload.itemId}`);
            await apiDelete(`/lists/${change.listId}/items/${change.payload.itemId}`);
            break;
          }
          case 'createList': {
            console.log(`[OfflineQueue] Syncing createList: "${change.payload.name}"`);
            await postJson(`/lists`, { name: change.payload.name });
            break;
          }
          case 'renameItem': {
            console.log(`[OfflineQueue] Syncing renameItem: ${change.payload.itemId}`);
            await putJson(`/lists/${change.listId}/items/${change.payload.itemId}`, {
              content: change.payload.content,
              properties: {},
            });
            // Re-run AI on the new text. A failure here shouldn't strand the
            // rename itself, which is already persisted above.
            try {
              await postJson(`/lists/${change.listId}/items/${change.payload.itemId}/reprocess`, {});
            } catch (reprocessError) {
              console.warn('[OfflineQueue] Rename synced but reprocess failed:', reprocessError?.message);
            }
            break;
          }
          case 'expandSubitems': {
            console.log(`[OfflineQueue] Syncing expandSubitems: ${change.payload.itemId}`);
            await postJson(`/lists/${change.listId}/items/${change.payload.itemId}/expand-subitems`, {});
            break;
          }
          case 'toggleSubitem': {
            console.log(`[OfflineQueue] Syncing toggleSubitem: ${change.payload.subitemId}`);
            await putJson(
              `/lists/${change.listId}/items/${change.payload.itemId}/subitems/${change.payload.subitemId}`,
              { updates: { is_completed: change.payload.is_completed } }
            );
            if (typeof change.payload.parentCompleted === 'boolean') {
              await putJson(`/lists/${change.listId}/items/${change.payload.itemId}`, {
                is_completed: change.payload.parentCompleted,
              });
            }
            break;
          }
          default:
            console.warn('[OfflineQueue] Unknown change type:', change.type);
        }

        // Success: remove from queue and clean up isPendingSync flags
        setCache(c => {
          let updated = removePendingChange(c, change.id);
          if (change.type === 'createItem' && change.listId) {
            const listItems = updated.items[change.listId] || [];
            updated = {
              ...updated,
              items: {
                ...updated.items,
                [change.listId]: listItems.filter(i => !(i.isPendingSync && i.tempId))
              }
            };
          }
          if (change.type === 'createList') {
            updated = {
              ...updated,
              lists: updated.lists.filter(l => !(l.isPendingSync && l.tempId))
            };
          }
          return updated;
        });
        synced++;
        console.log(`[OfflineQueue] Successfully synced change ${change.id}`);
      } catch (error) {
        console.error(`[OfflineQueue] Failed to sync change ${change.id}:`, error?.response?.status || error.message);
        const newRetryCount = (change.retryCount || 0) + 1;

        if (newRetryCount >= 3) {
          setCache(c => {
            let updated = removePendingChange(c, change.id);
            if (change.type === 'createItem' && change.listId) {
              const listItems = updated.items[change.listId] || [];
              updated = {
                ...updated,
                items: {
                  ...updated.items,
                  [change.listId]: listItems.filter(i => !(i.isPendingSync && i.tempId))
                }
              };
            }
            if (change.type === 'createList') {
              updated = {
                ...updated,
                lists: updated.lists.filter(l => !(l.isPendingSync && l.tempId))
              };
            }
            return updated;
          });
          failed++;
        } else {
          setCache(c => {
            const p = { ...(c.pendingChanges || {}) };
            if (p[change.id]) {
              p[change.id] = { ...p[change.id], retryCount: newRetryCount };
            }
            return { ...c, pendingChanges: p };
          });
          failed++;
        }
      }
    }

    isProcessingRef.current = false;

    if (synced > 0) {
      showNotification(`Synced ${synced} offline change${synced !== 1 ? 's' : ''}`, 'success');
    }
    if (failed > 0) {
      showNotification(`${failed} change${failed !== 1 ? 's' : ''} could not be synced`, 'error');
    }
  }, [setCache, showNotification]);

  // Keep a ref to processQueue for effects
  const processQueueRef = useRef(processQueue);
  useEffect(() => { processQueueRef.current = processQueue; }, [processQueue]);

  // Primary trigger: process queue on offline-to-online transition
  useEffect(() => {
    const wasOffline = !prevOnlineRef.current;
    prevOnlineRef.current = isOnline;

    if (isOnline && wasOffline) {
      console.log('[OfflineQueue] Detected offline→online transition, will process in 1s');
      const timer = setTimeout(() => {
        processQueueRef.current();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  // Fallback: poll every 5s while online with pending changes.
  // Catches edge cases where the transition trigger misses
  // (e.g., page refresh while offline, token not ready on first attempt).
  useEffect(() => {
    if (!isOnline) return;

    const interval = setInterval(() => {
      const pending = getPendingChanges(cacheRef.current);
      console.log(`[OfflineQueue] Poll tick — pending: ${pending.length}, processing: ${isProcessingRef.current}`);
      if (pending.length > 0 && !isProcessingRef.current) {
        processQueueRef.current();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isOnline]);
}
